<div class="p-4 md:p-6 space-y-6">
  <!-- Page Header -->
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-base-content tracking-tight">
        Payment Approvals
      </h1>
      <p class="text-xs sm:text-sm text-base-content/60 mt-1">
        Review student offline bank transfer payments and approve access to your
        classes.
      </p>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-btn-outline gap-2"
        (click)="refresh()"
        [disabled]="loading"
      >
        <i class="fa-solid fa-arrows-rotate" [class.animate-spin]="loading"></i>
        <span>Refresh</span>
      </button>
    </div>
  </div>

  <!-- Filters & Search Toolbar -->
  <div class="daisy-card bg-base-100 border border-base-200 shadow-sm p-4">
    <div
      class="flex flex-col md:flex-row md:items-center justify-between gap-3"
    >
      <!-- Status Filter Tabs -->
      <div class="daisy-join">
        <button
          type="button"
          class="daisy-join-item daisy-btn daisy-btn-sm"
          [class.daisy-btn-primary]="filterTab === 'ALL'"
          [class.daisy-btn-ghost]="filterTab !== 'ALL'"
          (click)="filterTab = 'ALL'"
        >
          All ({{ total }})
        </button>
        <button
          type="button"
          class="daisy-join-item daisy-btn daisy-btn-sm gap-1.5"
          [class.daisy-btn-primary]="filterTab === 'PENDING'"
          [class.daisy-btn-ghost]="filterTab !== 'PENDING'"
          (click)="filterTab = 'PENDING'"
        >
          <span>Pending</span>
          @if (pendingCount > 0) {
            <span
              class="daisy-badge daisy-badge-warning daisy-badge-xs font-semibold"
              >{{ pendingCount }}</span
            >
          }
        </button>
        <button
          type="button"
          class="daisy-join-item daisy-btn daisy-btn-sm"
          [class.daisy-btn-primary]="filterTab === 'APPROVED'"
          [class.daisy-btn-ghost]="filterTab !== 'APPROVED'"
          (click)="filterTab = 'APPROVED'"
        >
          Approved
        </button>
      </div>

      <!-- Search Input -->
      <div class="relative w-full md:w-72">
        <input
          type="text"
          class="daisy-input daisy-input-bordered daisy-input-sm w-full pl-8"
          placeholder="Search by ID, name, or phone..."
          [(ngModel)]="searchTerm"
        />
        <i
          class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-base-content/40"
        ></i>
        @if (searchTerm) {
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-base-content/40 hover:text-base-content"
            (click)="searchTerm = ''"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Payments Table -->
  <div
    class="daisy-card bg-base-100 border border-base-200 shadow-sm overflow-hidden"
  >
    @if (loading) {
      <div
        class="flex flex-col items-center justify-center py-16 text-base-content/60 gap-3"
      >
        <span
          class="daisy-loading daisy-loading-spinner daisy-loading-md text-primary"
        ></span>
        <span class="text-sm">Loading payments...</span>
      </div>
    } @else {
      <div class="overflow-x-auto">
        <table class="daisy-table daisy-table-sm w-full">
          <thead>
            <tr class="bg-base-200/50 text-base-content/70">
              <th>Payment ID</th>
              <th>Student / Billing</th>
              <th>Mode</th>
              <th>Amount</th>
              <th>Initiated Date</th>
              <th>Bank Slip</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (payment of filteredPayments; track payment._id) {
              <tr class="hover:bg-base-200/30 transition-colors">
                <!-- Payment ID -->
                <td
                  class="font-mono text-xs font-semibold text-base-content/80"
                >
                  {{ payment._id?.slice(-8) }}
                </td>

                <!-- Student / Billing Name -->
                <td>
                  <div class="flex flex-col">
                    <span class="font-medium text-sm text-base-content">
                      {{ getStudentName(payment) }}
                    </span>
                    @if (getStudentEmail(payment)) {
                      <span class="text-xs text-base-content/60">
                        {{ getStudentEmail(payment) }}
                      </span>
                    }
                    @if (getStudentContact(payment)) {
                      <span class="text-xs text-base-content/40">
                        {{ getStudentContact(payment) }}
                      </span>
                    }
                    @if (payment.itemTitle) {
                      <span class="text-[11px] text-primary font-medium mt-0.5">
                        <i class="fa-solid fa-graduation-cap mr-1"></i
                        >{{ payment.itemTitle }}
                      </span>
                    }
                  </div>
                </td>

                <!-- Payment Mode -->
                <td>
                  <span
                    class="daisy-badge daisy-badge-outline daisy-badge-sm font-medium gap-1"
                  >
                    @if (payment.modeOfPayment === 'BANK_TRANSFER') {
                      <i class="fa-solid fa-building-columns text-xs"></i> Bank
                      Transfer
                    } @else if (payment.modeOfPayment === 'PAYHERE') {
                      <i class="fa-solid fa-credit-card text-xs"></i> PayHere
                    } @else {
                      {{ payment.modeOfPayment || 'Unknown' }}
                    }
                  </span>
                </td>

                <!-- Amount -->
                <td class="font-bold text-sm text-base-content">
                  @if (payment.amount !== null) {
                    LKR {{ payment.amount | number: '1.2-2' }}
                  } @else {
                    —
                  }
                </td>

                <!-- Initiated Date -->
                <td class="text-xs text-base-content/60">
                  @if (payment.initiatedOn) {
                    {{ payment.initiatedOn | date: 'mediumDate' }}
                  } @else {
                    —
                  }
                </td>

                <!-- Bank Slip Preview -->
                <td>
                  @if (getSlipUrl(payment)) {
                    <a
                      [href]="getSlipUrl(payment)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-info gap-1"
                      title="Open bank transfer receipt"
                    >
                      @if (isSlipPdf(payment)) {
                        <i class="fa-solid fa-file-pdf text-error"></i>
                      } @else {
                        <i class="fa-solid fa-file-invoice"></i>
                      }
                      <span>View Slip</span>
                    </a>
                  } @else {
                    <span class="text-xs text-base-content/40 italic"
                      >No slip attached</span
                    >
                  }
                </td>

                <!-- Status Badge -->
                <td>
                  @if (payment.isApproved) {
                    <span
                      class="daisy-badge daisy-badge-success daisy-badge-sm font-medium gap-1"
                    >
                      <i class="fa-solid fa-check text-xs"></i> Approved
                    </span>
                  } @else {
                    <span
                      class="daisy-badge daisy-badge-warning daisy-badge-sm font-medium gap-1"
                    >
                      <i class="fa-solid fa-clock text-xs"></i> Pending Approval
                    </span>
                  }
                </td>

                <!-- Action Buttons -->
                <td class="text-right">
                  <div class="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      class="daisy-btn daisy-btn-ghost daisy-btn-xs"
                      (click)="openInfo(payment)"
                      title="View full payment details"
                    >
                      <i class="fa-solid fa-eye text-xs"></i>
                    </button>
                    @if (!payment.isApproved) {
                      <button
                        type="button"
                        class="daisy-btn daisy-btn-success daisy-btn-xs gap-1"
                        [disabled]="processing()"
                        (click)="openApproveConfirm(payment)"
                        title="Approve this payment"
                      >
                        <i class="fa-solid fa-check text-xs"></i>
                        <span>Approve</span>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="8" class="text-center py-16 text-base-content/40">
                  <div class="flex flex-col items-center gap-3">
                    <i class="fa-solid fa-receipt text-4xl"></i>
                    <p class="text-base font-semibold">No payments found</p>
                    <p class="text-xs">
                      @if (filterTab === 'PENDING') {
                        There are no pending payments requiring approval right
                        now.
                      } @else {
                        No payment transactions recorded for this workspace yet.
                      }
                    </p>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      @if (total > limit) {
        <div
          class="flex items-center justify-between px-4 py-3 border-t border-base-200"
        >
          <p class="text-xs text-base-content/60">
            Showing {{ (page - 1) * limit + 1 }} to
            {{ Math.min(page * limit, total) }} of {{ total }} payments
          </p>
          <div class="daisy-join">
            <button
              type="button"
              class="daisy-join-item daisy-btn daisy-btn-sm"
              [disabled]="page <= 1"
              (click)="onPageChange(page - 1)"
            >
              «
            </button>
            <button
              type="button"
              class="daisy-join-item daisy-btn daisy-btn-sm daisy-btn-active"
            >
              Page {{ page }}
            </button>
            <button
              type="button"
              class="daisy-join-item daisy-btn daisy-btn-sm"
              [disabled]="page * limit >= total"
              (click)="onPageChange(page + 1)"
            >
              »
            </button>
          </div>
        </div>
      }
    }
  </div>
</div>

<!-- Payment Info Details Modal -->
@if (showInfoModal && selectedPayment) {
  <dialog class="daisy-modal daisy-modal-open z-50">
    <div class="daisy-modal-box max-w-lg">
      <div
        class="flex items-center justify-between pb-3 border-b border-base-200"
      >
        <h3 class="font-bold text-lg text-base-content flex items-center gap-2">
          <i class="fa-solid fa-receipt text-primary"></i>
          Payment Details
        </h3>
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square"
          (click)="closeInfo()"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>

      <div class="py-4 space-y-4 text-sm">
        <!-- Overview Grid -->
        <div
          class="grid grid-cols-2 gap-3 bg-base-200/50 p-3 rounded-xl border border-base-200"
        >
          <div>
            <span class="text-xs text-base-content/60">Payment ID</span>
            <p
              class="font-mono text-xs font-semibold text-base-content truncate"
              [title]="selectedPayment._id"
            >
              {{ selectedPayment._id }}
            </p>
          </div>
          <div>
            <span class="text-xs text-base-content/60">Status</span>
            <p>
              @if (selectedPayment.isApproved) {
                <span class="daisy-badge daisy-badge-success daisy-badge-sm"
                  >Approved</span
                >
              } @else {
                <span class="daisy-badge daisy-badge-warning daisy-badge-sm"
                  >Pending</span
                >
              }
            </p>
          </div>
          <div>
            <span class="text-xs text-base-content/60">Mode</span>
            <p class="font-medium text-base-content">
              {{ selectedPayment.modeOfPayment }}
            </p>
          </div>
          <div>
            <span class="text-xs text-base-content/60">Amount</span>
            <p class="font-bold text-base text-primary">
              LKR {{ selectedPayment.amount | number: '1.2-2' }}
            </p>
          </div>
          <div>
            <span class="text-xs text-base-content/60">Initiated On</span>
            <p class="text-xs text-base-content">
              {{ selectedPayment.initiatedOn | date: 'medium' }}
            </p>
          </div>
          @if (selectedPayment.approvedOn) {
            <div>
              <span class="text-xs text-base-content/60">Approved On</span>
              <p class="text-xs text-base-content">
                {{ selectedPayment.approvedOn | date: 'medium' }}
              </p>
            </div>
          }
        </div>

        <!-- Student & Billing Info -->
        <div class="border border-base-200 rounded-xl p-3 space-y-2">
          <h4
            class="font-semibold text-xs uppercase tracking-wider text-base-content/60"
          >
            Student & Billing Details
          </h4>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between py-0.5">
              <span class="text-base-content/60">Student Name</span>
              <span class="font-medium text-base-content">{{
                getStudentName(selectedPayment)
              }}</span>
            </div>
            @if (getStudentEmail(selectedPayment)) {
              <div class="flex justify-between py-0.5">
                <span class="text-base-content/60">Email</span>
                <span class="font-medium text-base-content">{{
                  getStudentEmail(selectedPayment)
                }}</span>
              </div>
            }
            @if (getStudentContact(selectedPayment)) {
              <div class="flex justify-between py-0.5">
                <span class="text-base-content/60">Contact Number</span>
                <span class="font-medium text-base-content">{{
                  getStudentContact(selectedPayment)
                }}</span>
              </div>
            }
            @if (selectedPayment.itemTitle) {
              <div class="flex justify-between py-0.5">
                <span class="text-base-content/60">Course / Class</span>
                <span class="font-medium text-primary">{{
                  selectedPayment.itemTitle
                }}</span>
              </div>
            }
            @if (selectedPayment.billingInformation?.city) {
              <div class="flex justify-between py-0.5">
                <span class="text-base-content/60">City / Province</span>
                <span class="font-medium text-base-content">
                  {{ selectedPayment.billingInformation?.city }}
                  @if (selectedPayment.billingInformation?.province) {
                    , {{ selectedPayment.billingInformation?.province }}
                  }
                </span>
              </div>
            }
            @if (selectedPayment.bankData?.merchant) {
              <div class="flex justify-between py-0.5">
                <span class="text-base-content/60">Bank Merchant</span>
                <span class="font-mono text-base-content">{{
                  selectedPayment.bankData?.merchant
                }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Bank Slip Preview -->
        @if (getSlipUrl(selectedPayment)) {
          <div class="border border-base-200 rounded-xl p-3 space-y-3">
            <div class="flex items-center justify-between">
              <h4
                class="font-semibold text-xs uppercase tracking-wider text-base-content/60"
              >
                Bank Transfer Slip
              </h4>
              <a
                [href]="getSlipUrl(selectedPayment)"
                target="_blank"
                rel="noopener noreferrer"
                class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary gap-1"
              >
                <i class="fa-solid fa-arrow-up-right-from-square"></i> Open Full
                Receipt
              </a>
            </div>
            @if (isSlipPdf(selectedPayment)) {
              <div
                class="flex flex-col items-center justify-center p-6 bg-base-200/50 rounded-lg border border-dashed border-base-300 gap-2"
              >
                <i class="fa-solid fa-file-pdf text-error text-3xl"></i>
                <span class="text-xs text-base-content/70 font-medium"
                  >PDF Receipt Document</span
                >
                <a
                  [href]="getSlipUrl(selectedPayment)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="daisy-btn daisy-btn-outline daisy-btn-xs daisy-btn-primary gap-1 mt-1"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square"></i> View
                  PDF Slip
                </a>
              </div>
            } @else {
              <div
                class="rounded-lg overflow-hidden border border-base-200 bg-base-200/30 flex justify-center max-h-64"
              >
                <img
                  [src]="getSlipUrl(selectedPayment)"
                  alt="Bank Transfer Receipt"
                  class="object-contain max-h-64 w-auto rounded"
                />
              </div>
            }
          </div>
        }
      </div>

      <div class="daisy-modal-action border-t border-base-200 pt-3">
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm"
          (click)="closeInfo()"
        >
          Close
        </button>
        @if (!selectedPayment.isApproved) {
          <button
            type="button"
            class="daisy-btn daisy-btn-success daisy-btn-sm gap-1.5"
            (click)="closeInfo(); openApproveConfirm(selectedPayment)"
          >
            <i class="fa-solid fa-check"></i>
            <span>Approve Payment</span>
          </button>
        }
      </div>
    </div>
    <div class="daisy-modal-backdrop" (click)="closeInfo()"></div>
  </dialog>
}

<!-- Approve Confirm Modal -->
@if (showApproveConfirm && selectedPayment) {
  <dialog class="daisy-modal daisy-modal-open z-50">
    <div class="daisy-modal-box max-w-sm">
      <div
        class="w-12 h-12 rounded-full bg-success/10 text-success flex items-center justify-center mb-3"
      >
        <i class="fa-solid fa-check text-xl"></i>
      </div>
      <h3 class="font-bold text-lg text-base-content">Approve Payment?</h3>
      <p class="text-xs text-base-content/70 mt-2">
        Are you sure you want to approve this payment of
        <span class="font-bold text-base-content"
          >LKR {{ selectedPayment.amount | number: '1.2-2' }}</span
        >
        for
        <span class="font-medium text-base-content">{{
          getStudentName(selectedPayment)
        }}</span
        >?
      </p>
      <p class="text-xs text-base-content/50 mt-1">
        This will activate the student's subscription and grant access to the
        class.
      </p>
      <div class="daisy-modal-action">
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm"
          (click)="closeApproveConfirm()"
          [disabled]="processing()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="daisy-btn daisy-btn-success daisy-btn-sm gap-2"
          [disabled]="processing()"
          (click)="confirmApprove()"
        >
          @if (processing()) {
            <span
              class="daisy-loading daisy-loading-spinner daisy-loading-xs"
            ></span>
          }
          Approve
        </button>
      </div>
    </div>
    <div class="daisy-modal-backdrop" (click)="closeApproveConfirm()"></div>
  </dialog>
}
